'use client'

import Button from '@shared/components/Button/Button'
import Heading from '@shared/components/Heading/Heading'
import { GHOST_THRESHOLD, useIsTop } from '@/components/NavBar/hooks'
import Search from '@shared/components/Search/Search'
import Section from '@shared/components/Section/Section'
import Text from '@shared/components/Text/Text'
import { formatNumber, getUrl, isDev, urlify } from '@shared/lib/utils'
import { Columns, Grid, Row, Stack } from '@christiankaindl/lyts'
import { TabPanel, useTabStore } from '@ariakit/react'
import { motion, useSpring, useTransform } from 'framer-motion'
import {
  GitCompareIcon,
  LandmarkIcon,
  ScaleIcon,
  SearchIcon,
} from 'lucide-react'
import Link from 'next/link'
import { useTypewriter } from 'react-simple-typewriter'
import { pathMappings } from '@shared/lib/paths'
import { appName, isDeJurisdiction } from '@shared/lib/jurisdiction'
import { iconMappings } from '@/lib/iconMappings'
import Reel from '@/components/Reel/Reel'
import { card } from '@/styles/theme.css'
import * as styles from './index.css'
import { Suspense } from 'react'
import { Overlay, OverlayContent } from '@shared/components/Overlay/Overlay'
import { useSearchParams } from 'next/navigation'
import TabList from '@/components/Tabs/TabList'
import TabButton from '@/components/Tabs/TabButton'

const style = { maxWidth: '30em', width: '100%' }
const inputProps = { style: { paddingRight: 95 } }

export function SearchWithTypewriter() {
  // Same threshold as the NavBar, so the ⌘K shortcut is never claimed by both search fields
  const isTop = useIsTop(GHOST_THRESHOLD)
  const [text] = useTypewriter({
    words: [
      'Gesetzen',
      'Entscheidungen',
      'Verordnungen',
      'Rechtssätzen',
      'Verträgen',
      'OGH Urteilen',
      'Kundmachungen',
    ],
    loop: true,
    deleteSpeed: 35,
    typeSpeed: 125,
  })

  return (
    <Suspense fallback={<div style={{ height: 46 }} />}>
      <div className='fade-in'>
        <Search
          slackMsgEnvironmentPrefix={isDev ? '[DEV]' : '[PROD]'}
          placeholder={`Suche nach ${text}`}
          id='home-search'
          enableShortcut={isTop}
          autoFocus
          center
          style={style}
          inputProps={inputProps}
        />
      </div>
    </Suspense>
  )
}

export function FocusSearchButton() {
  function focusSearch(): void {
    window.scrollTo({ top: 0 })
    // @ts-expect-error Use the following global function, because calling .focus() or .click() doesn't trigger the suggestion popover
    window._workaroundOpenSearch()
  }

  return (
    <Button onClick={focusSearch} size='large' style={{ width: '100%' }}>
      <Row gap={0.5}>
        <SearchIcon size={20} /> Durchsuchen
      </Row>
    </Button>
  )
}

export function StatsSection() {
  const spring = useSpring(0, { damping: 20, mass: 0.1, restDelta: 0.0001 })
  const paras = useTransform(spring, (value) =>
    formatNumber(Math.ceil(146_442 * value)),
  )
  const entscheidungen = useTransform(spring, (value) =>
    formatNumber(Math.ceil(1_261_577 * value)),
  )
  const connections = useTransform(spring, (value) =>
    formatNumber(Math.ceil(6_307_885 * value)),
  )

  return (
    <Section style={{ textAlign: 'center' }} stackProps={{ xAlign: 'center' }}>
      <motion.div
        onViewportEnter={() => {
          spring.set(1)
        }}
      />
      <Columns style={{ width: '100%' }} collapseAt='54em' gap={3}>
        <Stack xAlign='center'>
          <div
            style={{
              height: 48,
              width: 48,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(228, 177, 95, 0.2)',
              borderRadius: 99,
              marginLeft: -6,
              marginTop: -6,
              marginBottom: -6,
              color: '#A48757',
              flexShrink: 0,
              fontWeight: 600,
              fontSize: 22,
            }}
          >
            §
          </div>
          <Heading h={1}>
            <motion.span>{paras}</motion.span>
            <br />
            <span style={{ opacity: 0.35 }}>Paragrafen</span>
          </Heading>
        </Stack>
        <Stack xAlign='center'>
          <div
            style={{
              height: 48,
              width: 48,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(228, 177, 95, 0.2)',
              borderRadius: 99,
              marginLeft: -6,
              marginTop: -6,
              marginBottom: -6,
              color: '#A48757',
              flexShrink: 0,
              fontWeight: 700,
            }}
          >
            <ScaleIcon size={24} />
          </div>
          <Heading h={1}>
            <motion.span>{entscheidungen}</motion.span>
            <br />
            <span style={{ opacity: 0.35 }}>
              Entscheidungen
              <br />
              &amp; Rechtssätze
            </span>
          </Heading>
        </Stack>
        <Stack xAlign='center'>
          <div
            style={{
              height: 48,
              width: 48,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(228, 177, 95, 0.2)',
              borderRadius: 99,
              marginLeft: -6,
              marginTop: -6,
              marginBottom: -6,
              color: '#A48757',
              flexShrink: 0,
              fontWeight: 700,
            }}
          >
            <GitCompareIcon size={24} />
          </div>
          <Heading h={1}>
            <motion.span>{connections}</motion.span>
            <br />
            <span style={{ opacity: 0.35 }}>Verknüpfungen</span>
          </Heading>
        </Stack>
      </Columns>
      <Stack gap={0.5} xAlign='center'>
        <Text color='secondary'>
          {appName} verknüpft das{' '}
          {isDeJurisdiction ? 'deutsche' : 'österreichische'} Recht miteinander,
          und macht es einfach durchsuchbar. Das Angebot an Inhalten wird stetig
          erweitert.
        </Text>
      </Stack>
    </Section>
  )
}
export function CategoriesSection() {
  const categories = Object.entries(pathMappings)
  const tabs = useTabStore({ defaultSelectedId: categories[0]?.[0] })

  return (
    <Stack>
      <div>
        <TabList store={tabs}>
          {categories.map(([key, { name }]) => (
            <TabButton key={key} id={key} store={tabs}>
              {name}
            </TabButton>
          ))}
        </TabList>
      </div>

      {categories.map(([categoryKey, category]) => {
        const isLawCategory = category.documentType.includes('law')
        const visiblePaths = Object.entries(category.paths).filter(
          ([, { hidden }]) => !hidden,
        )

        return (
          <TabPanel
            key={categoryKey}
            store={tabs}
            tabId={categoryKey}
            render={<Reel />}
          >
            <Stack
              style={{
                flexShrink: 0,
                maxWidth: '19em',
                padding: 0,
                scrollSnapAlign: 'center',
              }}
              xAlign='start'
            >
              <Text color='secondary'>{category.description}</Text>
            </Stack>
            <Grid
              style={{
                gridTemplateRows: 'auto auto auto',
                gridAutoFlow: isLawCategory ? 'row' : 'column',
                gridTemplateColumns: `repeat(${Math.ceil(visiblePaths.length / 3)}, 20em)`,
              }}
            >
              {visiblePaths.map(([key, { name, type, count }]) => {
                const Icon = isLawCategory
                  ? (iconMappings[key] ?? iconMappings.fallback)
                  : LandmarkIcon
                return (
                  <Row
                    key={key}
                    asChild
                    className={styles.cardItem}
                    style={{ width: '100%', paddingBottom: 18 }}
                  >
                    <Link
                      className={card}
                      href={
                        isLawCategory
                          ? getUrl(type)
                          : `/${categoryKey}/${urlify(name)}`
                      }
                    >
                      <Icon color='#A48757' size={20} width={20} />
                      <Heading
                        h={5}
                        maxLines={1}
                        style={{ fontFamily: 'var(--noto-font)' }}
                      >
                        {name}
                      </Heading>
                      {!isLawCategory && !!count && (
                        <Text
                          color='tertiary'
                          size='small'
                          style={{ marginLeft: 'auto' }}
                        >
                          {formatNumber(count)}
                        </Text>
                      )}
                    </Link>
                  </Row>
                )
              })}
            </Grid>
          </TabPanel>
        )
      })}
    </Stack>
  )
}

export function ContactOverlay({
  form,
  header,
}: {
  form: React.ReactNode
  header: React.ReactNode
}) {
  const params = useSearchParams()
  const utmCampaign = params.get('utm_campaign')
  const defaultOpen = utmCampaign === 'Banner1' || utmCampaign === 'VLI25'
  return (
    <Overlay defaultOpen={defaultOpen}>
      <OverlayContent title=''>
        <Stack gap={0.75}>
          <Row xAlign='center'>{header}</Row>
          <Text color='secondary' align='center' size='large'>
            Tritt persönlich mit uns in Kontakt
          </Text>
          <div />
          {form}
        </Stack>
      </OverlayContent>
    </Overlay>
  )
}
